import { useQuery } from '@tanstack/react-query';

import { useCurrentCompany } from './useCurrentCompany';
import { supabase } from '../lib/supabase';

export interface SaaSSubscription {
  id: string;
  company_id: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'cancelled' | 'past_due' | 'unpaid' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'paused';
  billing_cycle: 'monthly' | 'annual';
  price: number;
  discount_percent: number;
  discount_amount: number;
  billing_start_date: string;
  billing_end_date: string | null;
  trial_end_date: string | null;
  delayed_billing_months: number;
  cancel_at_period_end: boolean;
  cancellation_requested_at: string | null;
  cancellation_effective_date: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch SaaS subscription for current company
 */
export function useSaaSSubscription() {
  const { data: companyId } = useCurrentCompany();

  return useQuery<SaaSSubscription | null>({
    queryKey: ['saasSubscription', { companyId }],
    enabled: !!companyId,
    queryFn: async () => {
      if (!companyId) return null;

      const { data, error } = await supabase
        .from('saas_subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found
          return null;
        }
        throw error;
      }

      return data as SaaSSubscription | null;
    },
  });
}
